// Lookup Service - Service layer for lookup-related API operations

import axiosInstance from "../instance/axiosInstance";
import { LOOKUP_ENDPOINTS } from "../endpoints/lookup.endpoints";
import { handleApiError } from "../utils/errorHandler";

let supRegCountryOfficeHierarchyCache = null;
let hasSupRegCountryOfficeHierarchyCache = false;
let supRegCountryOfficeHierarchyRequest = null;

const resetSupRegCountryOfficeHierarchyCache = () => {
  supRegCountryOfficeHierarchyCache = null;
  hasSupRegCountryOfficeHierarchyCache = false;
  supRegCountryOfficeHierarchyRequest = null;
};

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  try {
    return localStorage.getItem("axon_auth_token") || null;
  } catch (e) {
    return null;
  }
};

// Helper function to create headers with auth token
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const LookupService = {
  // Get all regions
  getRegions: async () => {
    try {
      const response = await axiosInstance.get(LOOKUP_ENDPOINTS.GET_REGIONS, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get application used lookup with default hours and saving reasons
  getApplicationUsedHoursReasonData: async () => {
    try {
      const response = await axiosInstance.get(
        LOOKUP_ENDPOINTS.GET_APPLICATION_USED_HOURS_REASON_DATA,
        { headers: getAuthHeaders() },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get region, super region, country and office hierarchy
  getSupRegCountryOfficeDataByRegion: async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh && hasSupRegCountryOfficeHierarchyCache) {
      return supRegCountryOfficeHierarchyCache;
    }

    if (!forceRefresh && supRegCountryOfficeHierarchyRequest) {
      return supRegCountryOfficeHierarchyRequest;
    }

    supRegCountryOfficeHierarchyRequest = axiosInstance
      .get(LOOKUP_ENDPOINTS.GET_SUP_REG_COUNTRY_OFFICE_DATA_BY_REGION, {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        supRegCountryOfficeHierarchyCache = response.data;
        hasSupRegCountryOfficeHierarchyCache = true;
        return supRegCountryOfficeHierarchyCache;
      })
      .catch((error) => {
        throw handleApiError(error);
      })
      .finally(() => {
        supRegCountryOfficeHierarchyRequest = null;
      });

    return supRegCountryOfficeHierarchyRequest;
  },

  __resetSupRegCountryOfficeHierarchyCache:
    resetSupRegCountryOfficeHierarchyCache,

  // Get super regions by region ID
  getSuperRegionsByRegion: async (regionId) => {
    if (!regionId) {
      throw new Error("Region ID is required");
    }

    try {
      const response = await axiosInstance.get(
        LOOKUP_ENDPOINTS.GET_SUPER_REGIONS_BY_REGION(regionId),
        { headers: getAuthHeaders() },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get roles by region ID
  getRolesByRegion: async (regionId) => {
    if (!regionId) {
      throw new Error("Region ID is required");
    }

    try {
      const response = await axiosInstance.get(
        LOOKUP_ENDPOINTS.GET_ROLES(regionId),
        { headers: getAuthHeaders() },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get statuses
  getStatuses: async () => {
    try {
      const response = await axiosInstance.get(LOOKUP_ENDPOINTS.GET_STATUSES, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get countries by super region ID
  getCountriesBySuperRegion: async (superRegionId) => {
    if (!superRegionId) {
      throw new Error("Super Region ID is required");
    }

    try {
      const response = await axiosInstance.get(
        LOOKUP_ENDPOINTS.GET_COUNTRIES_BY_SUPER_REGION(superRegionId),
        { headers: getAuthHeaders() },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get saving reasons by application used ID
  getSavingReasons: async (applicationUsedId) => {
    if (!applicationUsedId) {
      throw new Error("Application Used ID is required");
    }

    try {
      const response = await axiosInstance.get(
        LOOKUP_ENDPOINTS.GET_SAVING_REASONS(applicationUsedId),
        { headers: getAuthHeaders() },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default LookupService;
